import { useEffect, useState } from "react";
import { api, fmtEUR, PROPERTY_TYPES, PROPERTY_STATUS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Building2, Upload, X, MapPin, ShieldAlert, Coins } from "lucide-react";
import { toast } from "sonner";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { planContext, handleGateError } from "@/lib/plan";
import { Abbr } from "@/components/Abbr";

const empty = {
  label: "", address: "", city: "", postal_code: "",
  type: "appartement", surface: 0, rooms: 1, status: "vacant",
  photos: [], monthly_charges: 0,
  ordures_menageres: 0, teom_mensualisee: false,
  dpe_class: "", dpe_date: "",
  zone_tendue: false, reference_rent_m2: 0,
  notes: "",
};

const DPE_CLASSES = ["A", "B", "C", "D", "E", "F", "G"];

export default function PropertiesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [rentControl, setRentControl] = useState(null);   // {name, since} si commune concernée
  const [chargeTotals, setChargeTotals] = useState({ rec: 0, prop: 0 });
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canWrite, limits, isAdmin } = planContext(user);

  const load = async () => {
    const { data } = await api.get("/properties");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { if (!canWrite) return; setForm(empty); setEditId(null); setRentControl(null); setChargeTotals({ rec: 0, prop: 0 }); setOpen(true); };
  const loadChargeTotals = async (pid) => {
    try {
      const { data } = await api.get(`/charges?property_id=${pid}`);
      let rec = 0, prop = 0;
      data.forEach((c) => { if (c.category === "recuperable") rec += Number(c.amount || 0); else prop += Number(c.amount || 0); });
      setChargeTotals({ rec, prop });
    } catch { setChargeTotals({ rec: 0, prop: 0 }); }
  };
  const openEdit = (p) => {
    setForm({
      ...empty, ...p,
      monthly_charges: p.monthly_charges ?? 0,
      ordures_menageres: p.ordures_menageres ?? 0,
      teom_mensualisee: !!p.teom_mensualisee,
      photos: Array.isArray(p.photos) ? p.photos : [],
      dpe_class: p.dpe_class || "",
      dpe_date: p.dpe_date || "",
      zone_tendue: !!p.zone_tendue,
      reference_rent_m2: p.reference_rent_m2 ?? 0,
      notes: p.notes ?? "",
    });
    setEditId(p.id);
    setOpen(true);
    loadChargeTotals(p.id);
    if (p.postal_code) lookupZone(p.postal_code);
  };

  // Zone IRL + encadrement des loyers déduits du code postal
  const lookupZone = async (postal) => {
    try {
      const { data } = await api.get(`/territoires/zone?postal_code=${encodeURIComponent(postal)}`);
      setRentControl(data.rent_control);
      // pré-cocher zone_tendue si applicable
      setForm((f) => ({ ...f, zone_tendue: !!data.rent_control || f.zone_tendue }));
    } catch { setRentControl(null); }
  };

  // Upload d'une photo — supporte multi-sélection, jusqu'à 10 photos par bien
  const uploadPhoto = async (fileList) => {
    if (!editId) {
      toast.error("Enregistrez d'abord le bien pour ajouter des photos");
      return;
    }
    setUploading(true);
    try {
      for (const file of Array.from(fileList).slice(0, 10 - form.photos.length)) {
        const fd = new FormData();
        fd.append("category", "property-photos");
        fd.append("ref_id", editId);
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, photos: [...f.photos, data.storage_path] }));
      }
      toast.success("Photo ajoutée");
    } catch (e) { toast.error("Erreur upload : " + (e.response?.data?.detail || e.message)); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const removePhoto = async (storagePath) => {
    // besoin de l'id file — on recharge via API
    try {
      const { data } = await api.get(`/properties/${editId}`);
      // trouver dans la collection files via la route serait plus propre,
      // pour l'instant on retire visuellement puis on synchronise en réenregistrant le bien
      setForm((f) => ({ ...f, photos: (data.photos || form.photos).filter((p) => p !== storagePath) }));
      toast.success("Photo retirée (enregistrez pour valider)");
    } catch (e) {
      setForm((f) => ({ ...f, photos: f.photos.filter((p) => p !== storagePath) }));
    }
  };

  const save = async () => {
    if (!canWrite) return;
    try {
      const payload = {
        ...form,
        surface: Number(form.surface || 0),
        rooms: Number(form.rooms || 0),
        monthly_charges: Number(form.monthly_charges || 0),
        ordures_menageres: Number(form.ordures_menageres || 0),
        reference_rent_m2: Number(form.reference_rent_m2 || 0),
        photos: form.photos || [],
      };
      if (editId) {
        await api.put(`/properties/${editId}`, payload);
        toast.success("Bien mis à jour");
      } else {
        await api.post("/properties", payload);
        toast.success("Bien ajouté");
      }
      setOpen(false); load();
    } catch (e) { if (!handleGateError(e, navigate)) toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
  };

  const remove = async (id) => {
    if (!canWrite) return;
    if (!window.confirm("Supprimer ce bien ?")) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Bien supprimé"); load();
    } catch (e) { if (!handleGateError(e, navigate)) toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
  };

  const atLimit = !isAdmin && items.length >= limits.properties;

  const statusBadge = (s) => {
    const m = PROPERTY_STATUS.find((x) => x.value === s) || PROPERTY_STATUS[1];
    return <Badge className={`${m.color} border-0 font-medium`}>{m.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="properties-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Biens</h1>
          <p className="mt-1 text-sm text-slate-500">Gérez votre parc immobilier ({items.length}).</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="property-new-btn" onClick={openNew} disabled={!canWrite || atLimit} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90">
              <Plus className="h-4 w-4 mr-2" /> Nouveau bien
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editId ? "Modifier le bien" : "Nouveau bien"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Désignation</Label>
                <Input data-testid="property-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex : Appartement Rue de Rivoli" />
              </div>
              <div className="md:col-span-2">
                <Label>Adresse</Label>
                <Input data-testid="property-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div>
                <Label>Ville</Label>
                <Input data-testid="property-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <Label>Code postal</Label>
                <Input data-testid="property-postal" value={form.postal_code} onChange={(e) => { setForm({ ...form, postal_code: e.target.value }); if (e.target.value.length >= 2) lookupZone(e.target.value); }} />
                {rentControl && (
                  <div className="mt-1 text-xs text-amber-800 flex items-start gap-1 bg-amber-50 border border-amber-200 rounded p-2" data-testid="rent-control-banner">
                    <ShieldAlert className="h-3 w-3 mt-0.5 shrink-0" />
                    <span><b>Encadrement des loyers</b> : {rentControl.name} (arrêté du {rentControl.since}). Consultez le loyer de référence sur le site de la préfecture avant de fixer votre prix.</span>
                  </div>
                )}
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="property-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="property-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROPERTY_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Surface (m²)</Label>
                <Input type="number" value={form.surface} onChange={(e) => setForm({ ...form, surface: e.target.value })} />
              </div>
              <div>
                <Label>Pièces</Label>
                <Input type="number" value={form.rooms} onChange={(e) => setForm({ ...form, rooms: e.target.value })} />
              </div>
              <div>
                <Label>Ordures ménagères annuelles (€)</Label>
                <Input data-testid="property-om" type="number" value={form.ordures_menageres} onChange={(e) => setForm({ ...form, ordures_menageres: e.target.value })} />
                <label className="mt-2 flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <input type="checkbox" data-testid="property-teom-monthly" checked={!!form.teom_mensualisee} onChange={(e) => setForm({ ...form, teom_mensualisee: e.target.checked })} />
                  <span>Mensualiser la <Abbr short="TEOM" full="Taxe d'Enlèvement des Ordures Ménagères" /> sur les quittances (1/12 par mois)</span>
                </label>
              </div>
              {editId && (
                <div className="md:col-span-2 border-t border-slate-100 pt-4" data-testid="property-charge-totals">
                  <Label>Dépenses enregistrées pour ce bien</Label>
                  <div className="flex flex-wrap gap-3 mt-2">
                    <div className="rounded-xl border border-teal-200 bg-teal-50/60 px-4 py-3 flex-1 min-w-[160px]">
                      <div className="text-xs text-teal-700 font-medium">Charges récupérables (locataire)</div>
                      <div className="font-heading font-bold text-xl text-teal-900" data-testid="property-total-recuperable">{fmtEUR(chargeTotals.rec)}</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex-1 min-w-[160px]">
                      <div className="text-xs text-slate-600 font-medium">Charges propriétaire (bailleur)</div>
                      <div className="font-heading font-bold text-xl text-slate-900" data-testid="property-total-proprietaire">{fmtEUR(chargeTotals.prop)}</div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Totaux calculés depuis les dépenses saisies dans la fiche du bien (onglet « Charges »).</p>
                </div>
              )}
              <div>
                <Label>Classe <Abbr short="DPE" full="Diagnostic de Performance Énergétique" /></Label>
                <Select value={form.dpe_class || "unset"} onValueChange={(v) => setForm({ ...form, dpe_class: v === "unset" ? "" : v })}>
                  <SelectTrigger data-testid="property-dpe-class"><SelectValue placeholder="Non renseigné" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">Non renseigné</SelectItem>
                    {DPE_CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date du <Abbr short="DPE" full="Diagnostic de Performance Énergétique" /></Label>
                <Input type="date" data-testid="property-dpe-date" value={form.dpe_date} onChange={(e) => setForm({ ...form, dpe_date: e.target.value })} />
              </div>
              {form.zone_tendue && (
                <div className="md:col-span-2">
                  <Label>Loyer médian de référence (€/m² — préfecture)</Label>
                  <Input type="number" step="0.01" data-testid="property-ref-rent" value={form.reference_rent_m2} onChange={(e) => setForm({ ...form, reference_rent_m2: e.target.value })} placeholder="ex : 27,15" />
                </div>
              )}
              <div className="md:col-span-2 border-t border-slate-100 pt-4">
                <Label>Photos du bien ({form.photos.length}/10)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.photos.map((p) => (
                    <div key={p} className="relative w-24 h-24 rounded overflow-hidden border border-slate-200 bg-slate-50">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/by-path?path=${encodeURIComponent(p)}`} alt="photo" className="w-full h-full object-cover" onError={(e) => e.currentTarget.style.opacity = 0.3} />
                      <button type="button" className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5" onClick={() => removePhoto(p)} data-testid={`photo-remove-${p}`}>
                        <X className="h-3 w-3 text-rose-600" />
                      </button>
                    </div>
                  ))}
                  {editId && form.photos.length < 10 && (
                    <label className="w-24 h-24 rounded border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-teal-500 hover:bg-teal-50 text-xs text-slate-500" data-testid="photo-upload-btn">
                      <Upload className="h-4 w-4 mb-1" />
                      {uploading ? "…" : "Ajouter"}
                      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhoto(e.target.files)} />
                    </label>
                  )}
                </div>
                {!editId && (
                  <p className="text-xs text-slate-500 mt-2">Enregistrez d'abord le bien pour ajouter des photos.</p>
                )}
              </div>
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button data-testid="property-save" onClick={save} className="bg-[hsl(var(--primary))]">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {atLimit && canWrite && (
        <div
          data-testid="property-limit-banner"
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
        >
          <span className="text-sm text-amber-900">
            Vous avez atteint la limite de <b>{limits.properties} bien(s)</b> de votre formule {limits.label}.
          </span>
          <Button
            size="sm"
            onClick={() => navigate("/app/abonnement")}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="property-limit-cta"
          >
            Augmenter ma limite
          </Button>
        </div>
      )}

      <Card className="border-slate-200 shadow-none overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="h-10 w-10 mx-auto text-slate-300" />
            <div className="mt-3 text-sm text-slate-500">Aucun bien pour le moment.</div>
            <Button onClick={openNew} disabled={!canWrite} className="mt-4 bg-[hsl(var(--primary))]"><Plus className="h-4 w-4 mr-2" />Ajouter un bien</Button>
          </div>
        ) : (
          <>
          <Table className="hidden sm:table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Bien</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Surface</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Charges</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`property-row-${p.id}`}>
                  <TableCell>
                    <button type="button" onClick={() => navigate(`/app/biens/${p.id}`)} className="text-left group" data-testid={`property-open-${p.id}`}>
                      <div className="font-medium text-slate-900 group-hover:text-teal-700 group-hover:underline">{p.label}</div>
                      <div className="text-xs text-slate-500">{p.address}, {p.postal_code} {p.city}</div>
                    </button>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700 capitalize">{p.type.replace("_", " ")}</TableCell>
                  <TableCell className="text-sm">{p.surface} m² · {p.rooms} p.</TableCell>
                  <TableCell>{statusBadge(p.status)}</TableCell>
                  <TableCell className="text-sm">{fmtEUR(p.monthly_charges)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/app/biens/${p.id}`)} data-testid={`property-charges-${p.id}`}><Coins className="h-4 w-4 mr-1" /> Charges</Button>
                    <Button variant="ghost" size="icon" disabled={!canWrite} onClick={() => openEdit(p)} data-testid={`property-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" disabled={!canWrite} onClick={() => remove(p.id)} data-testid={`property-delete-${p.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Vue mobile : cartes empilées */}
          <div className="sm:hidden divide-y divide-slate-100">
            {items.map((p) => (
              <div key={p.id} className="p-4" data-testid={`property-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.label}</div>
                    <div className="text-xs text-slate-500 truncate">{p.address}, {p.postal_code} {p.city}</div>
                  </div>
                  {statusBadge(p.status)}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div><span className="text-slate-500">Type : </span><span className="capitalize">{p.type.replace("_", " ")}</span></div>
                  <div><span className="text-slate-500">Surface : </span>{p.surface} m² · {p.rooms} p.</div>
                  <div><span className="text-slate-500">Charges : </span>{fmtEUR(p.monthly_charges)}</div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => navigate(`/app/biens/${p.id}`)} data-testid={`property-charges-m-${p.id}`}><Coins className="h-4 w-4 mr-1" /> Charges</Button>
                  <Button variant="outline" size="sm" className="flex-1" disabled={!canWrite} onClick={() => openEdit(p)} data-testid={`property-edit-m-${p.id}`}><Pencil className="h-4 w-4 mr-1" /> Modifier</Button>
                  <Button variant="outline" size="sm" disabled={!canWrite} onClick={() => remove(p.id)} data-testid={`property-delete-m-${p.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </Card>
    </div>
  );
}
